import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { OrderProduct } from '../types';

export function useOrderProducts(orderId: string) {
  const [orderProducts, setOrderProducts] = useState<OrderProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (orderId) {
      fetchOrderProducts();
    }
  }, [orderId]);

  const fetchOrderProducts = async () => {
    if (!orderId) {
      setOrderProducts([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('order_products')
        .select(`
          id,
          order_id,
          product_id,
          hoeveelheid,
          prijs_per_stuk,
          aangemaakt_op,
          product:producten (
            id,
            naam,
            beschrijving,
            prijs,
            leverancier_id,
            aangemaakt_op,
            levertijd_dagen,
            artikelnummer,
            materiaalsoort,
            kwaliteit,
            lengte_mm,
            breedte_mm,
            hoogte_mm
          )
        `)
        .eq('order_id', orderId);

      if (error) {
        throw error;
      }

      // Transform the data to match our types
      const orderProducts: OrderProduct[] = (data || []).map(item => {
        // First cast the raw data to any to avoid type checking during transformation
        const rawItem = item as any;
        
        return {
          id: rawItem.id,
          order_id: rawItem.order_id,
          product_id: rawItem.product_id,
          hoeveelheid: rawItem.hoeveelheid,
          prijs_per_stuk: rawItem.prijs_per_stuk,
          aangemaakt_op: rawItem.aangemaakt_op,
          bijgewerkt_op: rawItem.aangemaakt_op, // Use aangemaakt_op as bijgewerkt_op
          product: rawItem.product ? {
            ...rawItem.product,
            voorraad: 0, // Default value since column might not exist yet
            bijgewerkt_op: rawItem.product.aangemaakt_op,
            voorraad_bijgewerkt_op: rawItem.product.aangemaakt_op
          } : undefined
        };
      });

      setOrderProducts(orderProducts);
      setError(null);
    } catch (err) {
      console.error('Error fetching order products:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  return { orderProducts, loading, error, refetch: fetchOrderProducts };
}
